import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  AlertCircle, ArrowUpDown, Clock3, ExternalLink, Grid, Headphones, List, ListMusic,
  LoaderCircle, Music2, Pause, Play, RefreshCw, Repeat2, Search, Shuffle, SkipBack,
  SkipForward, Volume2, VolumeX, Wifi, WifiOff
} from "lucide-react";
import { api, soundcloudAssetUrl } from "../lib/api";
import { syncLikesInBrowser } from "../lib/soundcloud-likes";
import { onSoundCloudNetworkStatus, resetSoundCloudConnection } from "../lib/soundcloud-browser";
import { SoundCloudWidget, type MusicTransport } from "./SoundCloudWidget";
import type { SoundCloudLibrary, SoundCloudTrack } from "../types";

const pageSize = 100;
type SortMode = "soundcloud" | "reverse" | "title" | "artist" | "duration-asc" | "duration-desc";
type LibraryView = "list" | "grid";
type Connectivity = { state: "checking" | "online" | "offline"; latency?: number };
type SavedPlayback = {
  version: 1;
  currentId: string | null;
  time: number;
  wantsPlayback: boolean;
  shuffleQueue: string[];
  shuffleCursor: number;
};

const playbackStorageKey = "nkriz-soundcloud-playback-v1";

const sortOptions: { value: SortMode; label: string }[] = [
  { value: "soundcloud", label: "SoundCloud order" },
  { value: "reverse", label: "Oldest likes first" },
  { value: "title", label: "Title A–Z" },
  { value: "artist", label: "Artist A–Z" },
  { value: "duration-asc", label: "Shortest first" },
  { value: "duration-desc", label: "Longest first" }
];

function savedSort(): SortMode {
  const value = localStorage.getItem("nkriz-soundcloud-sort");
  return sortOptions.some((option) => option.value === value) ? value as SortMode : "soundcloud";
}

function savedView(): LibraryView {
  return localStorage.getItem("nkriz-soundcloud-view") === "grid" ? "grid" : "list";
}

function savedVolume(): number {
  const value = Number(localStorage.getItem("nkriz-soundcloud-volume") ?? ".8");
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : .8;
}

function savedRepeat(): "off" | "all" | "one" {
  const value = localStorage.getItem("nkriz-soundcloud-repeat");
  return value === "all" || value === "one" ? value : "off";
}

function savedPlayback(): SavedPlayback {
  try {
    const value = JSON.parse(localStorage.getItem(playbackStorageKey) ?? "null") as Partial<SavedPlayback> | null;
    if (!value || value.version !== 1) throw new Error("No saved playback");
    return {
      version: 1,
      currentId: typeof value.currentId === "string" ? value.currentId : null,
      time: Number.isFinite(value.time) && Number(value.time) >= 0 ? Number(value.time) : 0,
      wantsPlayback: value.wantsPlayback === true,
      shuffleQueue: Array.isArray(value.shuffleQueue) ? value.shuffleQueue.filter((id): id is string => typeof id === "string") : [],
      shuffleCursor: Number.isInteger(value.shuffleCursor) && Number(value.shuffleCursor) >= 0 ? Number(value.shuffleCursor) : 0
    };
  } catch {
    return { version: 1, currentId: null, time: 0, wantsPlayback: false, shuffleQueue: [], shuffleCursor: 0 };
  }
}

function shuffled(values: string[]): string[] {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index--) {
    const swap = Math.floor(Math.random() * (index + 1));
    [result[index], result[swap]] = [result[swap]!, result[index]!];
  }
  return result;
}

function freshShuffleQueue(ids: string[], currentId?: string): string[] {
  const remaining = ids.filter((id) => id !== currentId);
  return currentId && ids.includes(currentId) ? [currentId, ...shuffled(remaining)] : shuffled(ids);
}

function sortTracks(tracks: SoundCloudTrack[], sort: SortMode): SoundCloudTrack[] {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  return [...tracks].sort((a, b) => {
    if (sort === "reverse") return a.position - b.position;
    if (sort === "title") return collator.compare(a.title, b.title) || b.position - a.position;
    if (sort === "artist") return collator.compare(a.artist, b.artist) || collator.compare(a.title, b.title);
    if (sort === "duration-asc") return a.durationMs - b.durationMs || b.position - a.position;
    if (sort === "duration-desc") return b.durationMs - a.durationMs || b.position - a.position;
    return b.position - a.position;
  });
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function resolveDuration(duration: number, durationMs?: number): number {
  return Number.isFinite(duration) && duration > 0 ? duration : (durationMs ?? 0) / 1000;
}

function formatLikedAt(value?: string): string {
  if (!value) return "Like date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? "Like date unavailable" : `Liked ${date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`;
}

function Description({ text }: { text: string }) {
  const pieces = text.split(/(https?:\/\/[^\s]+)/g);
  return <p>{pieces.map((piece, index) => piece.startsWith("http")
    ? <a key={index} href={piece} target="_blank" rel="noreferrer">{piece}</a>
    : piece)}</p>;
}

export function SoundCloudApp({ onPlaybackChange }: { onPlaybackChange?: (playing: boolean) => void } = {}) {
  const restoredPlayback = useRef(savedPlayback());
  const [library, setLibrary] = useState<SoundCloudLibrary | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>(savedSort);
  const [view, setView] = useState<LibraryView>(savedView);
  const [connectivity, setConnectivity] = useState<Connectivity>({ state: "checking" });
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [currentId, setCurrentId] = useState<string | null>(restoredPlayback.current.currentId);
  const [playing, setPlaying] = useState(false);
  const [wantsPlayback, setWantsPlayback] = useState(restoredPlayback.current.wantsPlayback);
  const [time, setTime] = useState(restoredPlayback.current.time);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(savedVolume);
  const [muted, setMuted] = useState(false);
  const [shuffle, setShuffle] = useState(() => localStorage.getItem("nkriz-soundcloud-shuffle") === "true");
  const [repeat, setRepeat] = useState<"off" | "all" | "one">(savedRepeat);
  const [mediaError, setMediaError] = useState("");
  const [syncState, setSyncState] = useState<"idle" | "running" | "done" | "partial" | "error">("idle");
  const [syncMessage, setSyncMessage] = useState("");
  const [syncProgress, setSyncProgress] = useState({ completed: 0, total: 0, fraction: 0 });
  const syncRunningRef = useRef(false);
  useEffect(() => onSoundCloudNetworkStatus((message) => { if (syncRunningRef.current) setSyncMessage(message); }), []);
  const audioRef = useRef<HTMLAudioElement>(null);
  const widgetRef = useRef<MusicTransport>(null);
  const lastAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastTrackRef = useRef<string | null>(null);
  const [warmTrackId, setWarmTrackId] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const resumeRef = useRef(restoredPlayback.current.currentId
    ? { id: restoredPlayback.current.currentId, time: restoredPlayback.current.time }
    : null);
  const shuffleQueueRef = useRef(restoredPlayback.current.shuffleQueue);
  const shuffleCursorRef = useRef(restoredPlayback.current.shuffleCursor);

  const loadLibrary = useCallback(async (opts?: { preferNewest?: boolean }) => {
    const value = await api.soundcloudLibrary();
    setLibrary(value);
    setError("");
    setCurrentId((previous) => {
      if (opts?.preferNewest) {
        const newest = [...value.tracks].sort((a, b) => b.position - a.position)[0];
        return newest?.soundcloudId ?? previous;
      }
      if (previous && value.tracks.some((track) => track.soundcloudId === previous)) return previous;
      const newest = [...value.tracks].sort((a, b) => b.position - a.position)[0];
      return newest?.soundcloudId ?? null;
    });
    return value;
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadLibrary().catch((cause) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : "Could not load the music library");
    });
    return () => { cancelled = true; };
  }, [loadLibrary]);

  const recheckLikes = useCallback(async () => {
    if (syncRunningRef.current || !library) return;
    syncRunningRef.current = true;
    resetSoundCloudConnection();
    setSyncState("running");
    setSyncMessage("Checking SoundCloud through this browser…");
    setSyncProgress({ completed: 0, total: 0, fraction: 0 });
    try {
      const result = await syncLikesInBrowser(library, (progress) => { setSyncMessage(progress.message); setSyncProgress({ ...progress, fraction: progress.fraction ?? 0 }); });
      const added = result.newTracks ?? 0;
      setSyncMessage(result.message || (added ? `Added ${added} new like${added === 1 ? "" : "s"}.` : "Up to date."));
      setSyncState(result.audioFailures ? "partial" : "done");
      await loadLibrary();
    } catch (cause) {
      setSyncState("error");
      setSyncMessage(cause instanceof Error ? cause.message : "Could not sync likes");
      await loadLibrary().catch(() => undefined);
    } finally { syncRunningRef.current = false; }
  }, [library, loadLibrary]);

  const tracks = useMemo(() => sortTracks(library?.tracks ?? [], sort), [library, sort]);
  const filteredTracks = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return tracks;
    return tracks.filter((track) => [track.title, track.artist, track.genre, track.description]
      .some((value) => value?.toLocaleLowerCase().includes(normalized)));
  }, [query, tracks]);
  const currentTrack = useMemo(() => tracks.find((track) => track.soundcloudId === currentId) ?? tracks[0], [currentId, tracks]);
  const remotePlayback = Boolean(currentTrack && !currentTrack.audio);
  const getMedia = useCallback((): MusicTransport | null => remotePlayback ? widgetRef.current : audioRef.current, [remotePlayback]);

  useEffect(() => {
    if (!shuffle || !tracks.length || !currentTrack) return;
    const ids = tracks.map((track) => track.soundcloudId);
    const allowed = new Set(ids);
    const seen = new Set<string>();
    let queue = shuffleQueueRef.current.filter((id) => allowed.has(id) && !seen.has(id) && Boolean(seen.add(id)));
    if (!queue.includes(currentTrack.soundcloudId)) {
      queue = freshShuffleQueue(ids, currentTrack.soundcloudId);
    } else {
      queue = [...queue, ...shuffled(ids.filter((id) => !seen.has(id)))];
    }
    shuffleQueueRef.current = queue;
    shuffleCursorRef.current = Math.max(0, queue.indexOf(currentTrack.soundcloudId));
  }, [currentTrack?.soundcloudId, shuffle, tracks]);

  useEffect(() => {
    setWarmTrackId(null);
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (!currentTrack || !playing || connection?.saveData || ["slow-2g", "2g"].includes(connection?.effectiveType ?? "")) return;
    const timeout = window.setTimeout(() => {
      const index = tracks.findIndex((track) => track.soundcloudId === currentTrack.soundcloudId);
      const nextId = repeat === "one" ? currentTrack.soundcloudId : shuffle
        ? shuffleQueueRef.current[shuffleCursorRef.current + 1]
        : tracks[index + 1]?.soundcloudId ?? (repeat === "all" ? tracks[0]?.soundcloudId : undefined);
      const next = tracks.find((track) => track.soundcloudId === nextId);
      if (next?.audio && next.soundcloudId !== currentTrack.soundcloudId) setWarmTrackId(next.soundcloudId);
    }, 1_200);
    return () => clearTimeout(timeout);
  }, [currentTrack?.soundcloudId, playing, repeat, shuffle, tracks]);

  useEffect(() => () => { lastAudioRef.current?.pause(); }, []);

  useEffect(() => setVisibleCount(pageSize), [query, sort, view]);
  useEffect(() => localStorage.setItem("nkriz-soundcloud-sort", sort), [sort]);
  useEffect(() => localStorage.setItem("nkriz-soundcloud-view", view), [view]);
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || visibleCount >= filteredTracks.length) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisibleCount((value) => Math.min(value + pageSize, filteredTracks.length));
    }, { rootMargin: "300px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [filteredTracks.length, visibleCount]);

  useEffect(() => {
    const audio = audioRef.current;
    if (lastAudioRef.current && lastAudioRef.current !== audio) lastAudioRef.current.pause();
    lastAudioRef.current = audio;
    if (!currentTrack) return;
    if (lastTrackRef.current === currentTrack.soundcloudId && !remotePlayback) resumeRef.current = { id: currentTrack.soundcloudId, time };
    lastTrackRef.current = currentTrack.soundcloudId;
    if (resumeRef.current?.id !== currentTrack.soundcloudId) setTime(0);
    setDuration(currentTrack.durationMs / 1000);
    setMediaError("");
    // Changing src already starts the browser's load. Calling load() again
    // aborts that request and discards the next track's preloaded buffer.
    if (audio && wantsPlayback) void audio.play().catch(() => setPlaying(false));
  }, [currentTrack?.soundcloudId, remotePlayback]);

  useEffect(() => {
    const safeVolume = Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : .8;
    if (audioRef.current) audioRef.current.volume = safeVolume;
    localStorage.setItem("nkriz-soundcloud-volume", String(safeVolume));
  }, [currentTrack?.soundcloudId, volume]);

  useEffect(() => localStorage.setItem("nkriz-soundcloud-shuffle", String(shuffle)), [shuffle]);
  useEffect(() => localStorage.setItem("nkriz-soundcloud-repeat", repeat), [repeat]);

  useEffect(() => {
    if (!currentTrack) return;
    localStorage.setItem(playbackStorageKey, JSON.stringify({
      version: 1,
      currentId: currentTrack.soundcloudId,
      time,
      wantsPlayback,
      shuffleQueue: shuffleQueueRef.current,
      shuffleCursor: shuffleCursorRef.current
    } satisfies SavedPlayback));
  }, [currentTrack, shuffle, time, wantsPlayback]);

  useEffect(() => {
    const saveNow = () => {
      if (!currentTrack) return;
      localStorage.setItem(playbackStorageKey, JSON.stringify({
        version: 1,
        currentId: currentTrack.soundcloudId,
        time: getMedia()?.currentTime ?? time,
        wantsPlayback,
        shuffleQueue: shuffleQueueRef.current,
        shuffleCursor: shuffleCursorRef.current
      } satisfies SavedPlayback));
    };
    const visibility = () => { if (document.visibilityState === "hidden") saveNow(); };
    window.addEventListener("pagehide", saveNow);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      saveNow();
      window.removeEventListener("pagehide", saveNow);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [currentTrack, time, wantsPlayback]);

  const checkConnectivity = useCallback(async () => {
    setConnectivity({ state: "checking" });
    if (!navigator.onLine) {
      setConnectivity({ state: "offline" });
      return;
    }
    const started = performance.now();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8_000);
    try {
      await fetch(`https://soundcloud.com/favicon.ico?workspace-check=${Date.now()}`, {
        cache: "no-store",
        credentials: "omit",
        mode: "no-cors",
        referrerPolicy: "no-referrer",
        signal: controller.signal
      });
      setConnectivity({ state: "online", latency: Math.max(1, Math.round(performance.now() - started)) });
    } catch {
      setConnectivity({ state: "offline" });
    } finally {
      window.clearTimeout(timeout);
    }
  }, []);

  useEffect(() => { void checkConnectivity(); }, [checkConnectivity]);

  useEffect(() => {
    onPlaybackChange?.(playing);
    return () => onPlaybackChange?.(false);
  }, [onPlaybackChange, playing]);

  const playTrack = useCallback((track: SoundCloudTrack) => {
    setWantsPlayback(true);
    if (track.soundcloudId === currentTrack?.soundcloudId) {
      void getMedia()?.play().catch(() => setPlaying(false));
    } else {
      resumeRef.current = null;
      if (shuffle) {
        shuffleQueueRef.current = freshShuffleQueue(tracks.map((entry) => entry.soundcloudId), track.soundcloudId);
        shuffleCursorRef.current = 0;
      }
      setCurrentId(track.soundcloudId);
    }
  }, [currentTrack?.soundcloudId, shuffle, tracks]);

  const toggleShuffle = () => {
    setShuffle((value) => {
      const next = !value;
      if (next && currentTrack) {
        shuffleQueueRef.current = freshShuffleQueue(tracks.map((track) => track.soundcloudId), currentTrack.soundcloudId);
        shuffleCursorRef.current = 0;
      }
      return next;
    });
  };

  const togglePlayback = () => {
    const audio = getMedia();
    if (!audio || !currentTrack) return;
    if (audio.paused) {
      setWantsPlayback(true);
      void audio.play().catch(() => setPlaying(false));
    } else {
      setWantsPlayback(false);
      audio.pause();
    }
  };

  const stepTrack = useCallback((direction: -1 | 1) => {
    if (!tracks.length) return;
    const index = Math.max(0, tracks.findIndex((track) => track.soundcloudId === currentTrack?.soundcloudId));
    let nextTrack: SoundCloudTrack | undefined;
    if (shuffle && tracks.length > 1) {
      const ids = tracks.map((track) => track.soundcloudId);
      let queue = shuffleQueueRef.current;
      let cursor = queue.indexOf(currentTrack?.soundcloudId ?? "");
      if (cursor < 0) {
        queue = freshShuffleQueue(ids, currentTrack?.soundcloudId);
        cursor = 0;
      }
      let nextCursor = cursor + direction;
      if (nextCursor < 0) nextCursor = repeat === "all" ? queue.length - 1 : 0;
      if (nextCursor >= queue.length) {
        if (repeat !== "all") return;
        queue = shuffled(ids);
        if (queue.length > 1 && queue[0] === currentTrack?.soundcloudId) [queue[0], queue[1]] = [queue[1]!, queue[0]!];
        nextCursor = 0;
      }
      shuffleQueueRef.current = queue;
      shuffleCursorRef.current = nextCursor;
      nextTrack = tracks.find((track) => track.soundcloudId === queue[nextCursor]);
    } else {
      let nextIndex = index + direction;
      if (nextIndex < 0) nextIndex = repeat === "all" ? tracks.length - 1 : 0;
      if (nextIndex >= tracks.length) {
        if (repeat !== "all") return;
        nextIndex = 0;
      }
      nextTrack = tracks[nextIndex];
    }
    if (!nextTrack) return;
    setWantsPlayback(true);
    const media = getMedia();
    if (nextTrack.soundcloudId === currentTrack?.soundcloudId && media) {
      media.currentTime = 0;
      void media.play().catch(() => setPlaying(false));
    } else {
      resumeRef.current = null;
      setCurrentId(nextTrack.soundcloudId);
    }
  }, [currentTrack?.soundcloudId, repeat, shuffle, tracks]);

  const handleEnded = () => {
    const media = getMedia();
    if (repeat === "one" && media) {
      media.currentTime = 0;
      void media.play();
      return;
    }
    const atEnd = shuffle
      ? shuffleCursorRef.current >= shuffleQueueRef.current.length - 1
      : tracks.findIndex((track) => track.soundcloudId === currentTrack?.soundcloudId) === tracks.length - 1;
    if (atEnd && repeat === "off") {
      setPlaying(false);
      setWantsPlayback(false);
      return;
    }
    stepTrack(1);
  };

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const session = navigator.mediaSession;
    const setHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try { session.setActionHandler(action, handler); } catch { /* Unsupported action on this browser. */ }
    };
    setHandler("play", () => {
      setWantsPlayback(true);
      void getMedia()?.play().catch(() => setPlaying(false));
    });
    setHandler("pause", () => {
      setWantsPlayback(false);
      getMedia()?.pause();
    });
    setHandler("previoustrack", () => stepTrack(-1));
    setHandler("nexttrack", () => stepTrack(1));
    setHandler("seekbackward", (details) => {
      const media = getMedia(); if (media) media.currentTime = Math.max(0, media.currentTime - (details.seekOffset ?? 10));
    });
    setHandler("seekforward", (details) => {
      const media = getMedia(); if (media) media.currentTime = Math.min(media.duration || Infinity, media.currentTime + (details.seekOffset ?? 10));
    });
    setHandler("seekto", (details) => {
      const media = getMedia(); if (media && details.seekTime != null) media.currentTime = details.seekTime;
    });
    setHandler("stop", () => {
      setWantsPlayback(false);
      const media = getMedia(); media?.pause(); if (media) media.currentTime = 0;
    });
    return () => (["play", "pause", "previoustrack", "nexttrack", "seekbackward", "seekforward", "seekto", "stop"] as MediaSessionAction[])
      .forEach((action) => setHandler(action, null));
  }, [stepTrack, getMedia]);

  useEffect(() => {
    if (!("mediaSession" in navigator) || !currentTrack) return;
    if (!("MediaMetadata" in window)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.title,
      artist: currentTrack.artist,
      album: "SoundCloud likes",
      artwork: [{ src: soundcloudAssetUrl("artwork", currentTrack.soundcloudId) }]
    });
  }, [currentTrack]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = playing ? "playing" : "paused";
    if (!navigator.mediaSession.setPositionState || !Number.isFinite(resolveDuration(duration, currentTrack?.durationMs)) || !currentTrack) return;
    const mediaDuration = resolveDuration(duration, currentTrack.durationMs);
    if (mediaDuration <= 0) return;
    try { navigator.mediaSession.setPositionState({ duration: mediaDuration, playbackRate: getMedia()?.playbackRate ?? 1, position: Math.min(Math.max(0, time), mediaDuration) }); } catch { /* Metadata can race the media element. */ }
  }, [currentTrack, duration, playing, time]);

  if (error) return <div className="soundcloud-app soundcloud-state"><AlertCircle size={34} /><strong>Music library unavailable</strong><span>{error}</span><button onClick={() => window.location.reload()}>Try again</button></div>;
  if (!library) return <div className="soundcloud-app soundcloud-state"><LoaderCircle className="spin" size={34} /><strong>Loading your likes…</strong><span>Preparing the local SoundCloud library.</span></div>;

  const shownTracks = filteredTracks.slice(0, visibleCount);
  const effectiveDuration = Number.isFinite(duration) && duration > 0 ? duration : (currentTrack?.durationMs ?? 0) / 1000;
  const seekProgress = effectiveDuration > 0 ? Math.min(100, Math.max(0, time / effectiveDuration * 100)) : 0;
  const rangeStyle = { "--range-progress": `${seekProgress}%` } as CSSProperties;
  const volumeStyle = { "--range-progress": `${Math.min(100, Math.max(0, muted ? 0 : volume * 100))}%` } as CSSProperties;

  return <div className="soundcloud-app">
    <header className="soundcloud-header">
      <div className="soundcloud-brand"><span><Headphones size={21} /></span><div><strong>SoundCloud Player</strong><small>{tracks.length.toLocaleString()} liked tracks · private library</small></div></div>
      <label className="soundcloud-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search tracks, artists, genres, descriptions" aria-label="Search music library" />{query && <button onClick={() => setQuery("")} aria-label="Clear search">×</button>}</label>
    </header>

    <div className="soundcloud-body">
      <aside className="soundcloud-now">
        {currentTrack ? <>
          <div className="soundcloud-ambient" aria-hidden="true"><img crossOrigin="use-credentials" src={soundcloudAssetUrl("artwork", currentTrack.soundcloudId)} alt="" /></div>
          <div className="soundcloud-now-content">
          <div className="soundcloud-cover-wrap"><img crossOrigin="use-credentials" src={soundcloudAssetUrl("artwork", currentTrack.soundcloudId)} alt={`Artwork for ${currentTrack.title}`} /></div>
          <div className="soundcloud-now-copy">
            <small><span /> NOW PLAYING</small>
            <h2>{currentTrack.title}</h2>
            <h3>{currentTrack.artist}</h3>
            <div className="soundcloud-meta"><span><Clock3 size={13} />{formatLikedAt(currentTrack.likedAt)}</span>{currentTrack.genre && <span>{currentTrack.genre}</span>}</div>
            <a className="soundcloud-source" href={currentTrack.permalinkUrl} target="_blank" rel="noreferrer">Open on SoundCloud <ExternalLink size={13} /></a>
          </div>
          <section className="soundcloud-description"><strong>Description</strong>{currentTrack.description?.trim() ? <Description text={currentTrack.description} /> : <span>No description for this track.</span>}</section>
          {remotePlayback && <SoundCloudWidget key={currentTrack.soundcloudId} trackId={currentTrack.soundcloudId} title={currentTrack.title} duration={currentTrack.durationMs / 1000} wantsPlayback={wantsPlayback} volume={muted ? 0 : volume} startTime={resumeRef.current?.id === currentTrack.soundcloudId ? resumeRef.current.time : 0} transportRef={widgetRef} onReady={() => { resumeRef.current = null; setMediaError(""); }} onPlay={() => { setPlaying(true); setWantsPlayback(true); setMediaError(""); }} onPause={() => setPlaying(false)} onTime={setTime} onDuration={setDuration} onEnded={handleEnded} onError={(message) => { setPlaying(false); setMediaError(message); }} />}
          </div>
        </> : <div className="soundcloud-empty"><Music2 /><span>No tracks found.</span></div>}
      </aside>

      <section className="soundcloud-library">
        <div className="soundcloud-library-title">
          <div><ListMusic size={20} /><span><strong>{query ? "Search results" : "Liked tracks"}</strong><small>{filteredTracks.length.toLocaleString()} tracks · {sortOptions.find((option) => option.value === sort)?.label}</small></span></div>
          <div className="soundcloud-library-actions">
            <button
              className={`soundcloud-sync ${syncState}`}
              onClick={() => void recheckLikes()}
              disabled={syncState === "running"}
              title="Use this browser’s connection (including VeePN) to download new likes and upload them to your library"
              aria-label="Recheck profile likes"
            >
              <RefreshCw className={syncState === "running" ? "spin" : undefined} size={14} />
              <span>{syncState === "running" ? "Checking…" : "Recheck likes"}</span>
            </button>
            <a className="soundcloud-helper-link" href="/soundcloud-helper.html" target="_blank" rel="noreferrer" title="Set up Chrome/VeePN access for downloading likes">Browser helper</a>
            <button className={`soundcloud-connectivity ${connectivity.state}`} onClick={() => void checkConnectivity()} title="Check this browser’s connection to SoundCloud" aria-label="Check browser connection to SoundCloud">
              {connectivity.state === "checking" ? <RefreshCw className="spin" size={14} /> : connectivity.state === "online" ? <Wifi size={14} /> : <WifiOff size={14} />}
              <span>{connectivity.state === "checking" ? "Checking" : connectivity.state === "online" ? `SoundCloud ${connectivity.latency}ms` : "SoundCloud blocked"}</span>
            </button>
            <label className="soundcloud-sort" title="Sort tracks"><ArrowUpDown size={14} /><select value={sort} onChange={(event) => setSort(event.target.value as SortMode)} aria-label="Sort tracks">{sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <div className="soundcloud-view-toggle" aria-label="Library view">
              <button className={view === "list" ? "active" : ""} onClick={() => setView("list")} aria-label="List view" title="List view"><List size={16} /></button>
              <button className={view === "grid" ? "active" : ""} onClick={() => setView("grid")} aria-label="Grid view" title="Grid view"><Grid size={16} /></button>
            </div>
          </div>
        </div>
        {syncMessage && <div className={`soundcloud-sync-msg ${syncState}`} role="status"><span>{syncMessage}</span>{syncState === "running" && <><progress aria-label="Likes sync progress" max={Math.max(1, syncProgress.total)} value={syncProgress.total ? syncProgress.completed + syncProgress.fraction : undefined} /><small>Keep this player open and VeePN connected. {syncProgress.total ? `${syncProgress.completed}/${syncProgress.total} tracks` : "Checking latest likes…"}</small></>}{syncState === "error" && <a href="/soundcloud-browser-helper.zip" download>Download Browser helper</a>}</div>}
        <div className={`soundcloud-track-list ${view}`}>
          {shownTracks.map((track, index) => {
            const active = track.soundcloudId === currentTrack?.soundcloudId;
            return <button key={track.soundcloudId} className={`soundcloud-track ${active ? "active" : ""}`} onClick={() => playTrack(track)} aria-current={active ? "true" : undefined}>
              <span className="soundcloud-rank">{index + 1}</span>
              <span className="soundcloud-thumb"><img crossOrigin="use-credentials" loading="lazy" decoding="async" src={soundcloudAssetUrl("artwork", track.soundcloudId)} alt="" /><i className={active ? "shown" : ""}>{active && playing ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}</i></span>
              <span className="soundcloud-track-copy"><strong>{track.title}</strong><small>{track.artist}</small></span>
              <span className="soundcloud-track-genre">{track.genre || "—"}</span>
              <time>{formatTime(track.durationMs / 1000)}</time>
            </button>;
          })}
          {!shownTracks.length && <div className="soundcloud-empty"><Search size={26} /><strong>No matching tracks</strong><span>Try a title, artist, genre, or a word from the description.</span></div>}
          <div ref={loadMoreRef} className="soundcloud-load-more">{visibleCount < filteredTracks.length && <button onClick={() => setVisibleCount((value) => value + pageSize)}>Load more</button>}</div>
        </div>
      </section>
    </div>

    <footer className="soundcloud-player">
      <div className="soundcloud-player-track">{currentTrack && <><img crossOrigin="use-credentials" src={soundcloudAssetUrl("artwork", currentTrack.soundcloudId)} alt="" /><span><strong>{currentTrack.title}</strong><small>{currentTrack.artist}</small>{mediaError && <em>{mediaError}</em>}</span></>}</div>
      <div className="soundcloud-transport">
        <div className="soundcloud-buttons">
          <button className={shuffle ? "active" : ""} onClick={toggleShuffle} title={`Shuffle ${shuffle ? "on" : "off"}`} aria-label="Shuffle" aria-pressed={shuffle}><Shuffle size={16} /></button>
          <button onClick={() => stepTrack(-1)} title="Previous" aria-label="Previous track"><SkipBack size={19} fill="currentColor" /></button>
          <button className="primary" onClick={togglePlayback} title={playing ? "Pause" : "Play"} aria-label={playing ? "Pause" : "Play"}>{playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}</button>
          <button onClick={() => stepTrack(1)} title="Next" aria-label="Next track"><SkipForward size={19} fill="currentColor" /></button>
          <button className={repeat !== "off" ? "active" : ""} onClick={() => setRepeat((value) => value === "off" ? "all" : value === "all" ? "one" : "off")} title={`Repeat: ${repeat}`} aria-label={`Repeat: ${repeat}`} aria-pressed={repeat !== "off"}><Repeat2 size={16} />{repeat === "one" && <sup>1</sup>}</button>
        </div>
        <div className="soundcloud-progress"><time>{formatTime(time)}</time><input style={rangeStyle} type="range" min={0} max={Math.max(1, effectiveDuration)} step="0.1" value={Math.min(time, effectiveDuration || 0)} onChange={(event) => { const value = Number(event.target.value); const media = getMedia(); if (media) media.currentTime = value; setTime(value); }} aria-label="Track position" /><time>{formatTime(effectiveDuration)}</time></div>
      </div>
      <div className="soundcloud-volume"><button onClick={() => setMuted((value) => !value)} aria-label={muted ? "Unmute" : "Mute"}>{muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}</button><input style={volumeStyle} type="range" min={0} max={1} step="0.01" value={volume} onChange={(event) => { setVolume(Number(event.target.value)); setMuted(false); }} aria-label="Volume" /></div>
      {[...(currentTrack?.audio ? [currentTrack] : []), ...tracks.filter((track) => track.soundcloudId === warmTrackId && track.soundcloudId !== currentTrack?.soundcloudId && track.audio)].map((track) => <audio key={track.soundcloudId} ref={track.soundcloudId === currentTrack?.soundcloudId ? audioRef : undefined} data-track-id={track.soundcloudId} crossOrigin="use-credentials" preload="auto" src={soundcloudAssetUrl("audio", track.soundcloudId)} muted={track.soundcloudId !== currentTrack?.soundcloudId || muted} onLoadedMetadata={(event) => { if (event.currentTarget !== audioRef.current) return; const resume = resumeRef.current; if (resume?.id === track.soundcloudId) { const position = Math.min(Math.max(0, resume.time), Math.max(0, event.currentTarget.duration || track.durationMs / 1000)); event.currentTarget.currentTime = position; setTime(position); resumeRef.current = null; } }} onPlay={(event) => { if (event.currentTarget === audioRef.current) { setPlaying(true); setWantsPlayback(true); setMediaError(""); } }} onPause={(event) => { if (event.currentTarget === audioRef.current) setPlaying(false); }} onTimeUpdate={(event) => { if (event.currentTarget === audioRef.current) setTime(event.currentTarget.currentTime); }} onDurationChange={(event) => { if (event.currentTarget === audioRef.current) setDuration(event.currentTarget.duration); }} onError={(event) => { if (event.currentTarget === audioRef.current) { setPlaying(false); setMediaError("Couldn’t load this track from your server"); } }} onEnded={(event) => { if (event.currentTarget === audioRef.current) handleEnded(); }} />)}
    </footer>
  </div>;
}
